import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, fehlertext } from "./api";
import { Eingabe, Fehler, Feld, Karte, Knopf, Laedt, Seitenkopf } from "./ui";
import { sprache } from "./datum";

/**
 * Meine Zeit.
 *
 * Beantwortet drei Fragen und sonst keine: Wie viel habe ich diesen Monat
 * gearbeitet, wie viel haette ich sollen, und wie stehe ich insgesamt da.
 *
 * Gestempelt wird hier nicht. Das passiert auf "Mein Tag", und zwei Stellen
 * mit demselben Knopf sind eine zu viel: irgendwann steht auf der einen ein
 * anderer Zustand als auf der anderen, und dann traut niemand mehr beiden.
 *
 * Der Saldo steht bewusst mit seinen Bestandteilen da und nicht als eine
 * einzelne Zahl. Eine Ueberstundenzahl ohne die Rechnung dahinter erzeugt
 * genau die Diskussion, die sie verhindern sollte.
 */

interface Tag {
  tag: string;
  wochentag: number;
  feiertag: string | null;
  abwesenheit: string | null;
  istSekunden: number;
  sollSekunden: number;
}

interface Monat {
  monat: string;
  mitarbeiter: string;
  wochenstunden: number | null;
  istSekunden: number;
  sollSekunden: number | null;
  saldoSekunden: number | null;
  tage: Tag[];
}

interface Saldo {
  istSekunden: number;
  sollSekunden: number | null;
  saldoSekunden: number | null;
  wochenstunden: number | null;
  seit?: string;
}

export function MeineZeitSeite() {
  const { t, i18n } = useTranslation();
  const [monat, setMonat] = useState(() => new Date().toISOString().slice(0, 7));
  const [daten, setDaten] = useState<Monat | null>(null);
  const [saldo, setSaldo] = useState<Saldo | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    const [m, s] = await Promise.all([
      api.get<Monat>(`/zeiten/monat?monat=${monat}`),
      api.get<Saldo>("/zeiten/saldo")
    ]);
    setDaten(m);
    setSaldo(s);
  }, [monat]);

  useEffect(() => {
    void laden().catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [laden, t]);

  return (
    <>
      <Seitenkopf titel={t("meinezeit.titel")} />
      <Fehler text={fehler} />

      <div className="mb-4">
        <Feld label={t("zeit.monat")}>
          <Eingabe type="month" value={monat} onChange={(e) => setMonat(e.target.value)} />
        </Feld>
      </div>

      {!daten ? <Laedt /> : (
        <>
          <dl className="grid gap-4 sm:grid-cols-3">
            <Zahl label={t("meinezeit.ist")} wert={dauertext(daten.istSekunden)} />
            <Zahl
              label={t("meinezeit.soll")}
              wert={daten.sollSekunden === null ? "—" : dauertext(daten.sollSekunden)}
            />
            <Zahl
              label={t("meinezeit.saldoMonat")}
              wert={daten.saldoSekunden === null ? "—" : mitVorzeichen(daten.saldoSekunden)}
              betont={daten.saldoSekunden !== null}
              rot={(daten.saldoSekunden ?? 0) < 0}
            />
          </dl>

          {daten.wochenstunden === null ? (
            <Karte className="mt-4 border-l-4 border-marke p-4">
              <p className="text-sm">{t("meinezeit.ohneSoll")}</p>
            </Karte>
          ) : (
            saldo?.saldoSekunden !== null && saldo !== null && (
              <Karte className="mt-4 p-4">
                <p className="text-sm text-leise">
                  {t("meinezeit.saldoGesamt", { seit: saldo.seit ? kurz(saldo.seit) : "?" })}
                </p>
                <p className={`mt-1 text-2xl font-bold tabular-nums ${
                  (saldo.saldoSekunden ?? 0) < 0 ? "text-red-700" : ""
                }`}>
                  {mitVorzeichen(saldo.saldoSekunden ?? 0)}
                </p>
                <p className="mt-1 text-xs text-leise">
                  {t("meinezeit.rechnung", {
                    wochenstunden: daten.wochenstunden,
                    ist: dauertext(saldo.istSekunden),
                    soll: dauertext(saldo.sollSekunden ?? 0)
                  })}
                </p>
              </Karte>
            )
          )}

          <h2 className="mt-8 text-lg font-bold">{t("meinezeit.tage")}</h2>
          <ul className="mt-3 space-y-1">
            {daten.tage.map((tag) => (
              <Tageszeile key={tag.tag} tag={tag} sprache={i18n.language} />
            ))}
          </ul>
        </>
      )}
    </>
  );
}

/**
 * Ein Tag im Monat.
 *
 * Wochenenden und Feiertage bleiben blass, damit die Arbeitstage ins Auge
 * fallen. Ein Tag mit Soll, an dem nichts gestempelt wurde, wird rot: das ist
 * die Zeile, die der Mitarbeiter vergessen hat und die er als Korrektur
 * beantragen sollte.
 */
function Tageszeile({ tag, sprache }: { tag: Tag; sprache: string }) {
  const { t } = useTranslation();
  const frei = tag.wochentag === 0 || tag.wochentag === 6 || Boolean(tag.feiertag);
  const luecke = tag.sollSekunden > 0 && tag.istSekunden === 0;

  return (
    <li className={`flex flex-wrap items-baseline gap-x-3 rounded bg-white px-3 py-2 text-sm ${
      frei ? "opacity-60" : ""
    }`}>
      <span className="w-24 shrink-0 font-semibold tabular-nums">
        {new Date(`${tag.tag}T12:00:00Z`).toLocaleDateString(sprache, {
          weekday: "short", day: "2-digit", month: "2-digit"
        })}
      </span>
      {tag.feiertag && (
        <span className="rounded bg-grund px-2 py-0.5 text-xs font-semibold">{tag.feiertag}</span>
      )}
      {tag.abwesenheit && (
        <span className="rounded bg-marke/30 px-2 py-0.5 text-xs font-semibold">
          {t(`antrag.art.${tag.abwesenheit}`)}
        </span>
      )}
      <span className={`ms-auto tabular-nums ${luecke ? "font-semibold text-red-700" : ""}`}>
        {tag.istSekunden > 0 ? dauertext(tag.istSekunden) : luecke ? t("meinezeit.nichtGestempelt") : "—"}
      </span>
      <span className="w-20 shrink-0 text-end tabular-nums text-leise">
        {tag.sollSekunden > 0 ? dauertext(tag.sollSekunden) : ""}
      </span>
    </li>
  );
}

function Zahl({
  label, wert, betont, rot
}: { label: string; wert: string; betont?: boolean; rot?: boolean }) {
  return (
    <Karte className="p-4">
      <dt className="text-xs uppercase tracking-wide text-leise">{label}</dt>
      <dd className={`mt-1 text-2xl font-bold tabular-nums ${
        rot ? "text-red-700" : betont ? "text-verweis" : ""
      }`}>
        {wert}
      </dd>
    </Karte>
  );
}

function dauertext(sekunden: number): string {
  const s = Math.abs(Math.round(sekunden));
  return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")} h`;
}

function mitVorzeichen(sekunden: number): string {
  return `${sekunden < 0 ? "−" : "+"}${dauertext(sekunden)}`;
}

function kurz(tag: string): string {
  return new Date(`${tag}T12:00:00Z`).toLocaleDateString(sprache(), {
    day: "2-digit", month: "2-digit", year: "numeric"
  });
}
